import { calculateParentItemChanges } from "utils/calculations";
import { buildProductServiceItemDropdownOptions } from "./productServiceItemDropdown";
import { getUpstreamAddItemActionRootByProductServiceId } from "./wizardUpstreamRemovals";

const PS_ID_KEY = "document_item_product_service_id";

const recalcProductServiceLineTotal = (ps) =>
  Number(ps?.sell || 0) * Number(ps?.quantity || 1) - Number(ps?.discount || 0);

export const PRODUCT_SERVICE_FIELD_TOKEN = "__product_service__";

export const PRODUCT_SERVICE_FIELD_OPTIONS = [
  { code: "sell", name: "Sell" },
  { code: "quantity", name: "Quantity" },
  { code: "msrp", name: "MSRP" },
  { code: "map", name: "MAP" },
  { code: "cost", name: "Cost" },
  { code: "discount", name: "Discount" }
];

export const PRODUCT_SERVICE_FORMULA_MERGE_FIELDS = [
  { value: "[ProductService.Sell]", label: "Product Service: Sell" },
  { value: "[ProductService.Quantity]", label: "Product Service: Quantity" },
  { value: "[ProductService.MSRP]", label: "Product Service: MSRP" },
  { value: "[ProductService.MAP]", label: "Product Service: MAP" },
  { value: "[ProductService.Cost]", label: "Product Service: Cost" },
  { value: "[ProductService.Discount]", label: "Product Service: Discount" }
];

const PRODUCT_SERVICE_TEMPLATE_PREFIX = "template_ps:";
const PRODUCT_SERVICE_ADD_ITEM_PREFIX = "add_item_action:";

export const isProductServiceFieldToken = (value) => value === PRODUCT_SERVICE_FIELD_TOKEN;

export const encodeTemplateProductServiceRef = (documentItemProductServiceId) =>
  `${PRODUCT_SERVICE_TEMPLATE_PREFIX}${documentItemProductServiceId}`;

export const encodeAddItemActionProductServiceRef = (actionId) =>
  `${PRODUCT_SERVICE_ADD_ITEM_PREFIX}${actionId}`;

/**
 * Product/service dropdown for Change Document Value — includes bundle children
 * with encoded refs (template_ps / add_item_action).
 */
export function buildChangeDocumentValueProductServiceOptions({
  productServices,
  productServiceItemIds,
  layout,
  excludedTemplateIds = new Set(),
  nodes,
  edges,
  currentNodeId
}) {
  const addItemRootByPsId = getUpstreamAddItemActionRootByProductServiceId(nodes, edges, currentNodeId);
  const rawOptions = buildProductServiceItemDropdownOptions({
    productServices,
    productServiceItemIds,
    layout,
    excludedTemplateIds
  });

  return rawOptions.map((opt) => {
    if (opt.code === "subHeader") return opt;
    const psId = String(opt.code);
    const actionId = addItemRootByPsId.get(psId);
    return {
      ...opt,
      code: actionId
        ? encodeAddItemActionProductServiceRef(actionId)
        : encodeTemplateProductServiceRef(psId)
    };
  });
}

/**
 * Resolves a wizard product service ref to a document product service id.
 */
export function resolveProductServiceTargetId(productServiceRef, referenceProductServiceId, addItemActionProductServiceMap) {
  const parsedRef = parseProductServiceReference(productServiceRef);
  if (parsedRef.type === "template") {
    return referenceProductServiceId?.[parsedRef.id] || parsedRef.id || "";
  }
  if (parsedRef.type === "add_item_action") {
    return addItemActionProductServiceMap?.[String(parsedRef.id)] || "";
  }
  return "";
}

export function findProductServiceInTree(productServices, targetId) {
  if (!targetId) return null;
  let found = null;
  const walk = (ps) => {
    if (!ps || found) return;
    if (String(ps[PS_ID_KEY]) === String(targetId)) {
      found = ps;
      return;
    }
    (ps.children || []).forEach(walk);
  };
  (productServices || []).forEach(walk);
  return found;
}

/**
 * Applies a field update to a product service anywhere in the tree (including bundle children).
 * Recalculates bundle parent pricing when a child is updated.
 */
export function applyProductServiceFieldUpdateToTree(productServices, targetId, targetField, nextValue) {
  if (!targetId || !targetField) return productServices || [];

  const applyToNode = (ps) => {
    if (String(ps?.[PS_ID_KEY]) === String(targetId)) {
      const updated = { ...ps, [targetField]: nextValue, total: recalcProductServiceLineTotal({ ...ps, [targetField]: nextValue }) };
      return updated;
    }
    if (!ps?.children?.length) return ps;

    let changed = false;
    const children = ps.children.map((child) => {
      const updated = applyToNode(child);
      if (updated !== child) changed = true;
      return updated;
    });
    if (!changed) return ps;

    const withChildren = { ...ps, children };
    if (withChildren.is_bundle || children.length) {
      const priceChanges = calculateParentItemChanges(withChildren, withChildren.bundle_group, PS_ID_KEY);
      return {
        ...withChildren,
        ...priceChanges,
        total:
          Number(priceChanges.sell || 0) * Number(withChildren.quantity || 1) -
          Number(withChildren.discount || 0)
      };
    }
    return withChildren;
  };

  return (productServices || []).map(applyToNode);
}

export function applyQuantityChangesToTree(productServices, itemsToChangeQuantity) {
  if (!itemsToChangeQuantity || !Object.keys(itemsToChangeQuantity).length) {
    return productServices || [];
  }

  return Object.entries(itemsToChangeQuantity).reduce(
    (tree, [targetId, change]) =>
      applyProductServiceFieldUpdateToTree(tree, targetId, "quantity", change?.quantity),
    productServices || []
  );
}

export function recalculateProductServiceTotalsInTree(productServices) {
  const recalcNode = (ps) => {
    const children = (ps.children || []).map(recalcNode);
    const node = children.length ? { ...ps, children } : ps;
    return {
      ...node,
      total: Number(node.sell || 0) * Number(node.quantity || 1) - Number(node.discount || 0)
    };
  };

  return (productServices || []).map(recalcNode);
}

export const parseProductServiceReference = (value) => {
  const ref = String(value || "");
  if (ref.startsWith(PRODUCT_SERVICE_TEMPLATE_PREFIX)) {
    return {
      type: "template",
      id: ref.slice(PRODUCT_SERVICE_TEMPLATE_PREFIX.length)
    };
  }
  if (ref.startsWith(PRODUCT_SERVICE_ADD_ITEM_PREFIX)) {
    return {
      type: "add_item_action",
      id: ref.slice(PRODUCT_SERVICE_ADD_ITEM_PREFIX.length)
    };
  }
  return { type: "", id: "" };
};

